<template lang="pug">
.curso-main-container.pb-3
  BannerInterno
  .container.tarjeta.tarjeta--blanca.p-4.p-md-5.mb-5
    .titulo-principal.color-acento-contenido(data-aos="flip-up")
      .titulo-principal__numero
        span 1
      h1 Plan de implementación de bienestar animal y buenas prácticas ganaderas en équidos
    .row.align-items-center
      .col-lg-4.mb-4.mb-lg-0(data-aos="fade-right")
        figure
          img(src="@/assets/curso/tema1/img1.png", alt="", style="width: 380px").m-auto
      .col-lg-8(data-aos="fade-left")
        p El plan de implementación es el instrumento operativo central que permite aplicar, de forma organizada, sistemática y verificable, todos los principios de bienestar animal y los requisitos de las Buenas Prácticas Ganaderas (BPG) en équidos. No se trata de un simple requisito documental: es una hoja de ruta técnica que define qué debe hacerse, cómo ejecutarse, quién es responsable, qué recursos se requieren, con qué frecuencia se realizan las tareas y cómo se evaluará su cumplimiento.
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img1.svg', style="width: 150px").m-auto
          .col-lg.bg-c4
            .row.h-100.align-items-center
              .col-12
                .p-4
                  p.mb-0 Su función es traducir la normativa vigente en acciones concretas dentro de fincas, pesebreras, criaderos o centros ecuestres. En Colombia, este plan permite demostrar el cumplimiento de la Resolución ICA 7953 de 2017 (BPG Equinos), la Resolución ICA 136 de 2020 (Bienestar Animal por especie), la metodología oficial del ICA para evaluación de bienestar en équidos, así como la normativa sobre transporte y manejo humanitario.
        p.mb-0 De esta manera, los requisitos legales se convierten en procedimientos técnicos, medibles y aplicables en campo, abarcando aspectos esenciales como alimentación, hidratación, sanidad, bioseguridad, alojamiento, comportamiento, trabajo, descanso, movilización, interacción humano-animal y manejo de residuos. Su correcta ejecución garantiza un sistema de manejo seguro, eficiente, ético y centrado en la protección del bienestar físico y emocional de caballos, asnos y mulas.
    Separador
    #t_1_1
    .titulo-segundo.color-acento-contenido
      h2 1.1 Implementación en sistemas de producción equina: concepto general
    .row.align-items-center.mb-5
      .col-lg-8.order-2.order-lg-1(data-aos="fade-right")
        p El proceso de implementación en un sistema equino se refiere a la puesta en marcha, de manera organizada y progresiva, de todos los requisitos normativos, sanitarios y de bienestar animal, hasta consolidar un funcionamiento ordenado, sostenible y verificable. Implementar significa convertir los protocolos, normas y lineamientos en acciones reales mediante:
        .row.mb-3
          .col-12
            .p-4.bg-c5
              .row.align-items-center
                .col-lg-auto.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img2.svg', style="width: 280px").m-auto
                .col-lg
                  .p-4.bg-c6
                    ul.lista-ul.mb-0
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Pasos definidos.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Asignación de responsables.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Recursos adecuados.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Cronogramas de ejecución.
                      li.d-flex.mb-0
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Mecanismos de seguimiento.
        p.mb-0 Este proceso permite estandarizar el manejo diario, asegurando que actividades como alimentación, limpieza, movilización, atención sanitaria, manejo del dolor, bioseguridad y rutinas de trabajo se realicen siempre de forma coherente y correcta.
      .col-lg-4.mb-4.mb-lg-0.order-1.order-lg-2(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img2.png', style="width: 400px").m-auto
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 ¿Qué implica implementar Buenas Prácticas Ganaderas en équidos?
    p.mb-4 La implementación de BPG en équidos requiere integrar múltiples elementos dentro de la operación diaria de la unidad productiva. A continuación, se resumen los aspectos fundamentales que deben ser ejecutados de forma estructurada:
    .row.bg-c8
      .col-12
        .p-5
          .tarjeta.tarjeta--azul1.p-4.shadow
            SlyderA(tipo="b")
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img5.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Cumplimiento normativo de bienestar animal]
                  p.mb-0 Basado en: Resolución ICA 7953 de 2017 (BPG Equinos), Resolución ICA 136 de 2020, metodología oficial del ICA y estándares internacionales (OIE, FAO, ciencia del comportamiento equino).
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img6.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Protocolos obligatorios de bioseguridad y sanidad]
                  p.mb-0 Incluyen: control de ingreso/salida, cuarentena, limpieza y desinfección, manejo de plagas, plan sanitario, vacunación, desparasitación y registros clínicos.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img7.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Estandarización del manejo diario]
                  p.mb-0 Aplicada a: alimentación e hidratación, rutinas de trabajo y descanso, manejo de cascos y equipos, interacción humano-animal basada en manejo humanitario, y condiciones de alojamiento.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img8.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Ejecución de procedimientos organizados y registrados]
                  p.mb-0 Todas las actividades deben documentarse mediante Protocolos Operativos Estandarizados (POE), registros periódicos y procedimientos correctivos/preventivos.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img9.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Seguimiento mediante indicadores e inspecciones internas]
                  p.mb-0 Permite medir el grado de cumplimiento, identificar no conformidades, implementar acciones correctivas y mantener la trazabilidad del sistema.
    .row.bg-c9.mb-4
      .col-12
        .px-5.pt-5
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Propósitos de la implementación del plan
          .row.align-items-center.mb-4
            .col-lg.mb-4.mb-lg-0(data-aos="fade-right")
              p.mb-0 El objetivo central de implementar un plan de BPG y Bienestar Animal en sistemas equinos es lograr un sistema ambientalmente sostenible, económicamente viable y ético. A continuación, se resumen estos propósitos y los resultados que se esperan alcanzar:
            .col-lg-auto.d-none.d-lg-block(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img4.svg', style="width: 150px").m-auto
          .row.justify-content-center
            .col-md-6.col-lg-4.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img5.svg', style="width: 150px").m-auto
                p.text-center #[b Ambientalmente sostenible]
                p.text-center.mb-0 Gestión adecuada de estiércol y residuos, protección de cuerpos de agua, reducción de contaminación y olores, y uso eficiente de recursos.
            .col-md-6.col-lg-4.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img6.svg', style="width: 150px").m-auto
                p.text-center #[b Económicamente viable]
                p.text-center.mb-0 Mejor salud y longevidad animal, menor mortalidad, menos lesiones y enfermedades, mayor rendimiento en trabajo, reproducción o deporte, y reducción de costos sanitarios.
            .col-md-6.col-lg-4
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img7.svg', style="width: 150px").m-auto
                p.text-center #[b Ético, responsable y legal]
                p.text-center.mb-0 Cumplimiento normativo, bienestar físico y emocional del animal como eje del sistema, trazabilidad documental y protección de trabajadores, cuidadores y usuarios.
          Separador
          #t_1_2
          .titulo-segundo.color-acento-contenido
            h2 1.2 Objetivo del plan
          .row.align-items-center
            .col-lg-8.order-2.order-lg-1(data-aos="fade-right")
              p El plan de implementación es una herramienta operativa fundamental que permite aplicar, de manera estructurada, coherente y verificable, los principios de bienestar animal y las Buenas Prácticas Ganaderas (BPG) en una finca, pesebrera, criadero o centro ecuestre. Más que un documento formal, constituye una guía de gestión técnica que orienta y estandariza todas las actividades relacionadas con el manejo de équidos. En su estructura se define con exactitud:
              .row.mb-3
                .col-12
                  .p-4.bg-c5
                    .row.align-items-center
                      .col-lg-auto.mb-4.mb-lg-0
                        figure
                          img(src='@/assets/curso/tema1/img8.svg', style="width: 300px").m-auto
                      .col-lg
                        .p-4.bg-c6
                          ul.lista-ul.mb-0
                            li.d-flex
                              i.fa-solid.fa-circle-right
                              |
                              p.mb-0 Qué acciones deben realizarse.
                            li.d-flex
                              i.fa-solid.fa-circle-right
                              |
                              p.mb-0 Cómo deben ejecutarse.
                            li.d-flex
                              i.fa-solid.fa-circle-right
                              |
                              p.mb-0 Quiénes son los responsables.
                            li.d-flex
                              i.fa-solid.fa-circle-right
                              |
                              p.mb-0 Qué recursos se requieren.
                            li.d-flex
                              i.fa-solid.fa-circle-right
                              |
                              p.mb-0 Con qué frecuencia deben realizarse.
                            li.d-flex.mb-0
                              i.fa-solid.fa-circle-right
                              |
                              p.mb-0 Qué mecanismos de seguimiento y verificación asegurarán el cumplimiento.
              p.mb-0 Este plan evidencia el compromiso de la unidad productiva con la normatividad vigente en Colombia, específicamente:
            .col-lg-4.mb-4.mb-lg-0.order-1.order-lg-2(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img11.png', style="width: 380px").m-auto
    .row.mb-4
      .col-md-6.col-lg-3.mb-4.mb-lg-0
        .p-3.bg-c11
          .row.align-items-center
            .col-lg-auto.mb-4.mb-lg-0
              figure
                img(src='@/assets/curso/tema1/img9.svg', style="width: 80px").m-auto
            .col-lg
              p #[b 01]
              p.mb-0 Resolución ICA 7953 de 2017 (BPG equinos).
      .col-md-6.col-lg-3.mb-4.mb-lg-0
        .p-3.bg-c11
          .row.align-items-center
            .col-lg-auto.mb-4.mb-lg-0
              figure
                img(src='@/assets/curso/tema1/img9.svg', style="width: 80px").m-auto
            .col-lg
              p #[b 02]
              p.mb-0 Resolución ICA 136 de 2020 (bienestar animal por especie).
      .col-md-6.col-lg-3.mb-4.mb-md-0
        .p-3.bg-c11
          .row.align-items-center
            .col-lg-auto.mb-4.mb-lg-0
              figure
                img(src='@/assets/curso/tema1/img9.svg', style="width: 80px").m-auto
            .col-lg
              p #[b 03]
              p.mb-0 Lineamientos oficiales del ICA para la evaluación del bienestar en équidos.
      .col-md-6.col-lg-3
        .p-3.bg-c11
          .row.align-items-center
            .col-lg-auto.mb-4.mb-lg-0
              figure
                img(src='@/assets/curso/tema1/img9.svg', style="width: 80px").m-auto
            .col-lg
              p #[b 04]
              p.mb-0 Disposiciones nacionales sobre transporte y manejo humanitario.
    .row.align-items-center.mb-4
      .col-lg-4.mb-4.mb-lg-0(data-aos="fade-right")
        figure
          img(src='@/assets/curso/tema1/img12.png', style="width: 400px").m-auto
      .col-lg-8(data-aos="fade-left")
        p Su estructura permite traducir los requerimientos legales en procedimientos técnicos, claros, medibles y aplicables en campo, facilitando así auditorías, inspecciones y procesos de certificación. Además, el plan integra todos los componentes esenciales del manejo diario de los équidos, incluyendo:
        .row
          .col-12
            .p-4.bg-c12
              .row.align-items-center
                .col-lg-auto.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img10.svg', style="width: 300px").m-auto
                .col-lg
                  .p-4.bg-c6
                    ul.lista-ul.mb-0
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Alimentación.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Hidratación.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Sanidad.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Infraestructura.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Bioseguridad.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Transporte.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Rutinas de trabajo y descanso.
                      li.d-flex.mb-0
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Interacción humano-animal.
    .row.align-items-center
      .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
        p Su correcta ejecución garantiza un sistema de manejo organizado, seguro y ético, enfocado en:
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img11.svg', style="width: 120px").m-auto
          .col-lg.bg-c4
            .row.h-100.align-items-center
              .col-12
                .p-4
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Preservar el bienestar físico, emocional y comportamental de los animales.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Reducir los riesgos operativos y sanitarios.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Promover prácticas sostenibles.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Mejorar la productividad y la calidad de vida de los équidos.
      .col-lg-4(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img13.png', style="width: 400px").m-auto
    Separador
    #t_1_3
    .titulo-segundo.color-acento-contenido
      h2 1.3 Beneficios de implementar el plan
    .row.align-items-center.mb-4
      .col-lg-4.mb-4.mb-lg-0(data-aos="fade-right")
        figure
          img(src='@/assets/curso/tema1/img14.png', style="width: 400px").m-auto
      .col-lg-8(data-aos="fade-left")
        p La implementación de un plan estructurado de buenas prácticas equinas y bienestar animal ofrece beneficios integrales que impactan positivamente la eficiencia productiva, la seguridad operativa, el cumplimiento normativo y la calidad de vida de los animales.
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img12.svg', style="width: 120px").m-auto
          .col-lg.bg-c2
            .row.h-100.align-items-center
              .col-12
                .p-4
                  p.mb-0 Más allá de ser un requisito administrativo, este plan funciona como un sistema de gestión técnica, que organiza, orienta y estandariza todas las actividades relacionadas con el manejo equino. Esto permite alcanzar resultados sostenibles en el tiempo y responder con eficacia a las demandas sanitarias, productivas, ambientales y éticas del sector.
        p.mb-0 A continuación, se presentan los principales beneficios asociados a su implementación:
    .row.align-items-center
      .col-lg-9.mb-4.mb-lg-0(data-aos="fade-right")
        AcordionA(tipo="a" clase-tarjeta="tarjeta tarjeta--azul")
          .row(titulo="Unificación de criterios y estandarización operativa")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Claridad en los procedimientos y responsabilidades.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Reducción de la variabilidad en el manejo diario.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Facilidad para capacitar nuevo personal y actualizar al actual.
          .row(titulo="Mejora en el bienestar físico y emocional de los équidos")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Promoción de las cinco libertades del bienestar animal.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Reducción de lesiones, enfermedades, cojeras y problemas conductuales.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Mejora del estado corporal y general del animal.
          .row(titulo="Prevención de enfermedades y fortalecimiento de la bioseguridad")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Implementación de rutinas de limpieza, desinfección y control de vectores.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Detección temprana de enfermedades mediante registros y monitoreo.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Disminución del riesgo de brotes que afecten a animales y personas.
          .row(titulo="Reducción de riesgos y accidentes laborales")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Minimización de accidentes relacionados con animales, herramientas o instalaciones.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Medidas de seguridad claras para cuidadores, jinetes y visitantes.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Mejora del ambiente laboral y del orden operativo.
      .col-lg-3.d-none.d-lg-block(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img15.png', style="width: 300px").m-auto
    .row.align-items-center
      .col-lg-3.d-none.d-lg-block(data-aos="fade-right")
        figure
          img(src='@/assets/curso/tema1/img16.png', style="width: 300px").m-auto
      .col-lg-9.mb-4.mb-lg-0(data-aos="fade-left")
        AcordionA(tipo="a" clase-tarjeta="tarjeta tarjeta--azul")
          .row(titulo="Optimización del uso de recursos y mejora del desempeño")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Uso eficiente de recursos humanos, físicos y económicos.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Mejores resultados en reproducción, trabajo o deporte.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Disminución de costos por enfermedades, desperdicio o fallas operativas.
          .row(titulo="Facilitación de auditorías, inspecciones y certificaciones")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Evidencia documental del cumplimiento normativo.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Agilidad en auditorías internas y externas.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Apoyo en procesos de certificación en BPG y bienestar animal.
          .row(titulo="Incremento de la confianza y reputación de la unidad productiva")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Imagen profesional, responsable y alineada con estándares modernos.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Confianza por parte de propietarios, clientes y autoridades.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Valor agregado en servicios como entrenamiento, venta o turismo.
          .row(titulo="Contribución a la sostenibilidad ambiental y social")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Uso responsable del agua y manejo adecuado de residuos.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Reducción de impactos ambientales y conflictos con la comunidad.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Fomento de una cultura de ética, respeto y responsabilidad.
    Separador
    #t_1_4
    .titulo-segundo.color-acento-contenido
      h2 1.4 Elementos del plan de implementación
    .row.align-items-center.mb-4
      .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img13.svg', style="width: 140px").m-auto
          .col-lg.bg-c2
            .row.h-100.align-items-center
              .col-12
                .p-4
                  p.mb-0 El diagnóstico inicial constituye la base fundamental del plan de implementación, ya que permite conocer de forma precisa, objetiva y verificable las condiciones reales en que se encuentran los equinos, el personal, las instalaciones y los procedimientos de manejo. Este análisis integral identifica las brechas en el cumplimiento normativo, en las Buenas Prácticas Equinas y en los principios de bienestar animal. Así, se establecen prioridades de intervención y los ajustes operativos necesarios.
        p Este diagnóstico debe ser realizado por personal competente y debe contar con registro documental de todos los hallazgos, clasificados como críticos, moderados o de mejora continua, lo cual facilita la toma de decisiones, la asignación de recursos y la planificación del cronograma de acciones correctivas.
        p.mb-0 A continuación, se describen los componentes clave del diagnóstico inicial:
      .col-lg-4(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img17.png', style="width: 400px").m-auto
    .row.bg-c8
      .col-12
        .p-5
          .tarjeta.tarjeta--azul1.p-4.shadow
            SlyderA(tipo="b")
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img18.png', style="width: 400px").m-auto
                .col-lg-7
                  p #[b a) Instalaciones]
                  p Se evalúan todas las áreas donde permanecen o transitan los equinos para garantizar condiciones seguras, higiénicas y adecuadas para su bienestar. Se revisan:
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Pesebreras y boxes:] dimensiones, ventilación, iluminación, temperatura, humedad, limpieza, camas, drenaje y eliminación de olores.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Corrales, establos y zonas de trabajo:] pisos antideslizantes, materiales, seguridad estructural, ausencia de elementos filosos.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Potreros y praderas:] calidad del pasto, rotación, drenaje, lodo, sombras, riego, estado de cercas.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Áreas de ejercicio y adiestramiento:] picaderos, pistas, caminadores, rondadores, zonas de calentamiento/enfriamiento.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Equipamiento fijo:] comederos, bebederos, saladeros, tanques de agua, almacenamiento de alimentos.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Seguridad general:] señalización, orden, accesos, circulación vehicular, cuarentena, manejo de nuevos ingresos.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img19.png', style="width: 400px").m-auto
                .col-lg-7
                  p #[b b) Condición sanitaria]
                  p Evalúa la salud general del hato equino y la eficacia del plan sanitario:
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Historial clínico:] cólicos, cojeras, enfermedades infecciosas, parasitarias o respiratorias.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Vacunación y desparasitación:] frecuencia, cumplimiento, productos utilizados y registros.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Bioseguridad:] protocolos de ingreso/salida, aislamiento, desinfección de herramientas, vehículos, etc.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Control de vectores:] presencia de moscas, mosquitos, garrapatas; uso de trampas o tratamientos.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Acceso a servicios veterinarios:] atención de urgencias, visitas programadas, supervisión general.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Registro de tratamientos:] control de medicamentos, fechas, dosis, prescripciones y periodos de retiro.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img20.png', style="width: 400px").m-auto
                .col-lg-7
                  p #[b c) Condición corporal y estado físico]
                  p Se analiza el bienestar individual del animal mediante indicadores objetivos:
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Condición corporal (BCS):] uso de escalas (1-9 o 1-5).
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Cascos:] equilibrio, desgaste, grietas, herrajes, recortes.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Dentadura:] desgaste, puntas, dificultad para masticar, pérdida de peso.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Piel y pelaje:] heridas, hongos, dermatitis, mudas anormales, lesiones.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Locomoción:] cojera, rigidez, dolor al movimiento, desempeño en el trabajo.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Comportamiento y emocionalidad:] estrés, agresividad, apatía, estereotipias.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Estado general:] hidratación, respiración, mucosas, frecuencia cardiaca, condición reproductiva.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img21.png', style="width: 400px").m-auto
                .col-lg-7
                  p #[b d) Forma de trabajo]
                  p Analiza las condiciones laborales de cada equino:
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Finalidad:] deporte, carga, turismo, equinoterapia, exhibición, reproducción, recreación.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Rutinas:] duración de jornadas, intensidad del ejercicio, descansos.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Agua y alimentación:] acceso y calidad durante la jornada.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Equipos:] monturas, cabezadas, colleras, arneses; ajuste y estado.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Riesgos laborales:] superficies, temperatura, carga física o técnica.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Desempeño y fatiga:] signos de agotamiento, pérdida de condición, lesiones frecuentes.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img22.png', style="width: 400px").m-auto
                .col-lg-7
                  p #[b e) Nivel de formación del personal]
                  p Evalúa la competencia del personal responsable del manejo y cuidado:
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Manejo seguro y empático.]
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Detección de signos de dolor, enfermedad o estrés.]
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Capacitación en primeros auxilios y emergencias.]
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Uso correcto de herramientas y EPP.]
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Aplicación de protocolos de bioseguridad.]
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Formación normativa:] Resolución ICA 136 de 2020 y complementarias.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 #[b Responsabilidad y disciplina:] puntualidad, registros, comunicación.
    .row.bg-c9.mb-4
      .col-12
        .px-5.pt-5
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Interpretación de resultados del diagnóstico
          .row.align-items-center.mb-4
            .col-lg.mb-4.mb-lg-0(data-aos="fade-right")
              p.mb-0 Los hallazgos del diagnóstico se deben clasificar en tres niveles de prioridad, lo cual permite estructurar adecuadamente el plan de acción, definir responsables, priorizar recursos y establecer un cronograma de implementación.
            .col-lg-auto.d-none.d-lg-block(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img14.svg', style="width: 150px").m-auto
          .row.justify-content-center
            .col-md-6.col-lg-4.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img15.svg', style="width: 150px").m-auto
                p.text-center #[b Críticos]
                p.text-center.mb-0 Condiciones que afectan directamente la vida, salud o bienestar del animal. Requieren intervención inmediata.
            .col-md-6.col-lg-4.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img16.svg', style="width: 150px").m-auto
                p.text-center #[b Moderados]
                p.text-center.mb-0 No representan riesgo inmediato, pero requieren mejoras en el corto plazo.
            .col-md-6.col-lg-4
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img17.svg', style="width: 150px").m-auto
                p.text-center #[b De mejora continua]
                p.text-center.mb-0 Ajustes que optimizan la operación y elevan los estándares de bienestar.
          Separador
          #t_1_5
          .titulo-segundo.color-acento-contenido
            h2 1.5 Objetivos específicos y metas
          .row.align-items-center
            .col-lg-8.order-2.order-lg-1(data-aos="fade-right")
              p Los objetivos del plan de implementación deben formularse con base en el criterio SMART (específicos, medibles, alcanzables, relevantes y con un plazo definido). Cada objetivo debe estar acompañado de los siguientes elementos:
              .row.justify-content-center.mb-3
                .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img18.svg', style="width: 120px").m-auto
                .col-lg.bg-c13
                  .row.h-100.align-items-center
                    .col-12
                      .p-4
                        ul.lista-ul.mb-0
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Indicador: cómo se medirá el logro.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Línea base: situación inicial detectada en el diagnóstico.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Meta: nivel de mejora o resultado esperado.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Fecha límite de cumplimiento.
                          li.d-flex.mb-0
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Responsable asignado.
              p.mb-0 Estos objetivos orientan las decisiones operativas de la unidad productiva y permiten evaluar la efectividad del sistema de bienestar animal y las Buenas Prácticas Equinas. Ejemplos de objetivos medibles:
            .col-lg-4.mb-4.mb-lg-0.order-1.order-lg-2(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img23.png', style="width: 380px").m-auto
    .row.bg-c14
      .col-12
        .px-5
          .row.align-items-center.mb-5
            .col-lg-3.mb-4.mb-lg-0(data-aos="fade-right")
              figure
                img(src='@/assets/curso/tema1/img24.png', style="width: 300px").m-auto
            .col-lg-9(data-aos="fade-left")
              SlyderF(columnas="col-md-6 col-xl-4")
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img19.svg", alt="alt").img100.m-auto
                  p.text-center #[b Mejorar la condición corporal del lote]
                  p.text-center.mb-0 Del 60 % al 85 % en ocho meses.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img19.svg", alt="alt").img100.m-auto
                  p.text-center #[b Reducir la incidencia de cojeras]
                  p.text-center.mb-0 Disminución del 40 % en seis meses.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img19.svg", alt="alt").img100.m-auto
                  p.text-center #[b Cumplir con el 100 % del plan sanitario]
                  p.text-center.mb-0 Vacunación y desparasitación completas en seis meses.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img19.svg", alt="alt").img100.m-auto
                  p.text-center #[b Estandarizar el protocolo de limpieza]
                  p.text-center.mb-0 Implementación total en los primeros 30 días.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img19.svg", alt="alt").img100.m-auto
                  p.text-center #[b Capacitar al personal en bienestar animal y bioseguridad]
                  p.text-center.mb-0 100 % capacitado en dos meses.
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Objetivos específicos alineados con la normativa vigente
          p Los objetivos deben estar directamente relacionados con las exigencias del ICA y los estándares internacionales de bienestar animal, asegurando un enfoque integral y sostenible del sistema.
          .titulo-sexto.color-acento-contenido
            p.mb-0 #[b Tabla 1.] Normas y objetivos
    .tabla-a.color-acento-botones.mb-5
      table
        thead
          tr
            th Norma o estándar
            th Objetivo alineado
        tbody
          tr
            td Resolución ICA 7953 de 2017
            td.txt-p1 Cumplir con los 5 dominios del bienestar animal
          tr
            td Protocolos Operativos Estandarizados (POE) del ICA
            td.txt-p1 Implementar y mantener los POE actualizados
          tr
            td Plan sanitario obligatorio
            td.txt-p1 Aplicar vacunas, desparasitaciones y mantener registros clínicos
          tr
            td Requisitos de alojamiento
            td.txt-p1 Garantizar condiciones mínimas: área, ventilación, cama, iluminación
          tr
            td Lineamientos ICA/OIE
            td.txt-p1 Cumplir normas para manejo, preparación y transporte de équidos
          tr
            td Resolución ICA 136 de 2020
            td.txt-p1 Certificar la capacitación de todo el personal
          tr
            td Formato 3-1099 V2 y otros registros
            td.txt-p1 Organizar y mantener los documentos trazables y exigidos
          tr
            td Auditorías y certificaciones
            td.txt-p1 Demostrar cumplimiento y trazabilidad en visitas oficiales
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Acciones y actividades concretas
    .row.align-items-center.mb-4
      .col-lg.mb-4.mb-lg-0(data-aos="fade-right")
        p.mb-0 A continuación, se presenta un video que detalla las acciones y actividades concretas necesarias para implementar un plan de bienestar équido, abordando aspectos clave como la alimentación, el acceso al agua, la higiene de las instalaciones, la sanidad preventiva, el manejo conductual y las condiciones de trabajo del animal.
      .col-lg-auto.d-none.d-lg-block(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img14.svg', style="width: 150px").m-auto
    figure
      .video
        iframe(width="560" height="315" src="https://www.youtube.com/embed/Gm0YZNEZPow?si=KKv0IBJlDwe3-Att" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen)
    Separador
    #t_1_6
    .titulo-segundo.color-acento-contenido
      h2 1.6 Parámetros
    .row.align-items-center.mb-4
      .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img20.svg', style="width: 140px").m-auto
          .col-lg.bg-c2
            .row.h-100.align-items-center
              .col-12
                .p-4
                  p.mb-0 Los parámetros técnicos son herramientas fundamentales para evaluar de manera objetiva el estado de bienestar de los équidos y el cumplimiento de las Buenas Prácticas Pecuarias (BPP). Se definen como valores, rangos e indicadores que permiten identificar desviaciones, establecer comparaciones entre periodos y orientar decisiones correctivas o preventivas. Estos parámetros no solo sustentan el sistema de evaluación interna, sino que también sirven como referencia para auditorías externas y procesos de certificación oficial.
        p.mb-0 La correcta aplicación de estos parámetros garantiza que las condiciones físicas, sanitarias, ambientales y de manejo cumplan con los estándares mínimos exigidos por la normatividad vigente, promoviendo un sistema productivo equino ético, eficiente y sostenible.
      .col-lg-4(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img26.png', style="width: 400px").m-auto
    .row.bg-c9.mb-4
      .col-12
        .px-5
          .titulo-sexto.color-acento-contenido
            p.mb-0 #[b Tabla 2.] Principales parámetros técnicos del bienestar equino
          .tabla-a.color-acento-botones.mb-5
            table
              thead
                tr
                  th(width="30%") Parámetro
                  th Criterio técnico recomendado
              tbody.bg-white
                tr
                  th Condición corporal (BCS)
                  td.txt-p1 Escala de 1 a 5 o de 1 a 9. Rango óptimo: 2,5 a 3,5. Ajustado según tipo de actividad (trabajo, deporte, gestación o crecimiento).
                tr
                  th Espacio mínimo en pesebreras
                  td.txt-p1 Referencia técnica: 3 x 3 m para caballos adultos. Ajustable según alzada, peso, raza y temperamento.
                tr
                  th Cama o sustrato
                  td.txt-p1 Material seco, limpio, absorbente y cómodo (viruta, cascarilla, paja, arena fina). Reposición diaria de zonas húmedas.
                tr
                  th Acceso al agua
                  td.txt-p1 Agua limpia, fresca y continua. Mínimo: 20-40 litros diarios por animal. Bebederos sin algas ni sedimentos.
                tr
                  th Alimentación
                  td.txt-p1 Forraje fraccionado a lo largo del día. Consumo mínimo de 1,5-2 % del peso corporal (materia seca). Concentrados ajustados a edad y actividad.
                tr
                  th Trabajo y carga
                  td.txt-p1 Cargas máximas entre el 10 y 20 % del peso corporal. Jornadas adaptadas a condición corporal, clima y edad. Pausas frecuentes. Prohibición de sobrecarga.
                tr
                  th Ambiente y confort
                  td.txt-p1 Ventilación natural o mecánica. Temperatura ideal: entre 10 °C y 25 °C. Zonas de sombra en potreros.
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Normativa técnica aplicable
          .row.align-items-center.mb-4
            .col-lg.mb-4.mb-lg-0(data-aos="fade-right")
              p.mb-0 El uso de parámetros técnicos está respaldado por la legislación nacional, en particular las resoluciones emitidas por el ICA, que establecen criterios mínimos de bienestar, bioseguridad y sanidad para predios dedicados a la producción equina.
            .col-lg-auto.d-none.d-lg-block(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img21.svg', style="width: 150px").m-auto
          .row.justify-content-center.mb-5
            .col-md-6.col-lg-3.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img21.svg', style="width: 150px").m-auto
                p.text-center #[b Resolución 7953 de 2017]
                p.text-center.mb-0 Sanidad, bioseguridad, alimentación, manejo y bienestar animal.
            .col-md-6.col-lg-3.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img21.svg', style="width: 150px").m-auto
                p.text-center #[b Resolución 136 de 2020]
                p.text-center.mb-0 Condiciones mínimas de bienestar animal por especie.
            .col-md-6.col-lg-3.mb-4.mb-md-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img21.svg', style="width: 150px").m-auto
                p.text-center #[b Resolución 20223040006915 de 2022]
                p.text-center.mb-0 Requisitos para el transporte de équidos.
            .col-md-6.col-lg-3
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img21.svg', style="width: 150px").m-auto
                p.text-center #[b Metodología oficial ICA]
                p.text-center.mb-0 Indicadores directos e indirectos para evaluación técnica del bienestar.
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Lista de chequeo oficial para producción equina
          p.mb-0 Esta lista, utilizada en inspecciones y procesos de certificación, consolida los componentes obligatorios que deben verificarse en cada predio.
    .row.bg-c8.mb-5
      .col-12
        .p-5
          .tarjeta.tarjeta--azul1.p-4.shadow
            SlyderA(tipo="b")
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img27.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Sanidad animal y bioseguridad]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Registro del predio y de los animales ante el ICA.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Identificación individual obligatoria.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Plan sanitario firmado por veterinario: vacunación, desparasitación, manejo de cólico y enfermedades comunes.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Área de cuarentena con protocolo de ingreso (21 días).
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Control de ingreso: registro de vehículos, personas y animales; señalización y desinfección según nivel de riesgo.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img28.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Infraestructura y bioseguridad física]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Delimitación del predio y señalización de áreas restringidas.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Instalaciones adecuadas: pesebreras, pisos antideslizantes, iluminación, ventilación y drenaje.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Limpieza diaria y plan de desinfección escrito.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img29.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Agua, alimentación e inocuidad]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Acceso permanente a agua potable.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Análisis de calidad anual.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Forraje diario: mínimo 1,5-2 % del peso corporal.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Almacenamiento adecuado de alimentos y prohibición de residuos.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Suplementación con productos registrados ante ICA.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img30.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Insumos pecuarios y medicamentos]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Almacenamiento separado y adecuado.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Uso de productos con Registro ICA, no vencidos.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Control de medicamentos restringidos con prescripción oficial.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Registro completo de tratamientos: fecha, dosis, animal, tiempo de retiro.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img31.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Manejo de residuos y control de plagas]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Manejo diario de estiércol y prevención de contaminación.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Disposición final de residuos conforme a norma ambiental.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Programa de control de plagas con registros y seguimiento.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img32.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Bienestar animal]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Cumplimiento de las cinco libertades.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Acceso a potreros o áreas de ejercicio.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Jornadas de trabajo según límites establecidos (máximo 8 horas o 60 km).
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Equipos en buen estado. Prohibición de castigos o prácticas abusivas.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Transporte regulado: vehículos certificados, densidad adecuada, acceso a agua y descanso.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img33.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Personal]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Capacitación en bienestar animal, bioseguridad, manejo seguro y primeros auxilios.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Examen médico anual y ausencia de enfermedades transmisibles.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Uso obligatorio de EPP según actividad.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Servicios básicos: agua potable, baños y área de alimentación.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Afiliación del personal al sistema de seguridad social.
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Recursos para la implementación de las Buenas Prácticas Ganaderas en équidos
    .row.align-items-center.mb-4
      .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img22.svg', style="width: 120px").m-auto
          .col-lg.bg-c4
            .row.h-100.align-items-center
              .col-12
                .p-4
                  p.mb-0 La implementación de las Buenas Prácticas Ganaderas (BPG) en predios dedicados a la producción primaria de équidos,  exige la disponibilidad de recursos organizados, suficientes y adaptados a las condiciones del sistema productivo. Estos recursos son fundamentales para cumplir con los requisitos sanitarios, de bioseguridad, bienestar animal, inocuidad e infraestructura establecidos por el Instituto Colombiano Agropecuario (ICA).
        p Aunque cada unidad equina puede tener un enfoque diferente (deporte, trabajo, turismo, cría, reproducción, tracción o manejo de residuos), todas deben cumplir con los estándares mínimos definidos por la normatividad vigente, garantizando así el bienestar de los animales, la seguridad del personal y la sostenibilidad de la producción.
        P.mb-0 A continuación, se presentan los principales componentes y recursos requeridos para la implementación adecuada de las BPG:
      .col-lg-4(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img34.png', style="width: 400px").m-auto
    .row.bg-c8.mb-5
      .col-12
        .p-5
          .tarjeta.tarjeta--azul1.p-4.shadow
            SlyderA(tipo="b")
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img35.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Manejo sanitario]
                  p.mb-0 Prevención, diagnóstico y control de enfermedades. Plan sanitario firmado por médico veterinario. Registros de vacunas, tratamientos, cólicos, cojeras y lesiones. Control de vectores. Manejo de animales enfermos en zonas de aislamiento.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img36.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Uso de medicamentos veterinarios]
                  p.mb-0 Solo productos con Registro ICA. Prohibido el uso de sustancias vencidas o no autorizadas. Prescripción obligatoria en antibióticos, anestésicos y sedantes. Registro detallado de cada tratamiento. Jeringas desechables. Control de temperatura para vacunas.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img37.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Bioseguridad]
                  p.mb-0 Delimitación y señalización del predio. Control de ingreso con registros. Cuarentena de 21 días para animales nuevos. Protocolos de limpieza y desinfección. Separación funcional de áreas productivas.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img38.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Almacenamiento de insumos]
                  p.mb-0 Bodegas techadas, ventiladas, limpias y organizadas. Separación entre medicamentos, alimentos y herramientas. Control de humedad, plagas e inventario. Clasificación y custodia de medicamentos.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img39.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Alimentación y agua]
                  p.mb-0 Suministro permanente de agua potable. Forraje de calidad: 1,5-2 % del peso corporal. Aporte de nutrientes según etapa productiva. Prohibición de residuos contaminantes. Limpieza de comederos y bebederos. Almacenamiento bajo techo, separado del piso.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img40.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Seguridad y salud en el trabajo]
                  p.mb-0 Uso obligatorio de EPP (guantes, botas, casco, gafas, overol). Capacitación en bienestar animal, bioseguridad, primeros auxilios, manejo seguro y riesgos laborales. Examen médico anual. Acceso a instalaciones sanitarias. Afiliación a seguridad social.
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Finalidades y beneficios de las BPG aplicadas a équidos
    .row.align-items-center.mb-5
      .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
        p Las Buenas Prácticas Ganaderas tienen como propósito:
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img23.svg', style="width: 130px").m-auto
          .col-lg.bg-c4
            .row.h-100.align-items-center
              .col-12
                .p-4
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Garantizar animales sanos, seguros y con bienestar.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Promover un entorno de trabajo seguro para el personal y terceros.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Minimizar riesgos sanitarios y ambientales.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Asegurar sostenibilidad en la producción (deporte, trabajo, turismo, reproducción, etc.).
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Cumplir con los estándares exigidos por las autoridades sanitarias y facilitar la certificación del predio.
      .col-lg-4(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img41.png', style="width: 400px").m-auto
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Protocolos estandarizados en la unidad productiva
    p.mb-4 Todo predio debe contar con protocolos escritos y actualizados, que definan claramente las rutinas operativas, responsables y recursos asignados.
    .row.bg-c14.mb-4
      .col-12
        .px-5
          .row.align-items-center.mb-5
            .col-lg-3.mb-4.mb-lg-0(data-aos="fade-right")
              figure
                img(src='@/assets/curso/tema1/img42.png', style="width: 300px").m-auto
            .col-lg-9(data-aos="fade-left")
              SlyderF(columnas="col-md-6 col-xl-4")
                .tarjeta.tarjeta--slyder
                  .row.justify-content-center.mb-3
                    .col-12
                      img(src="@/assets/curso/tema1/img43.png", alt="alt").img100.m-auto
                  .row
                    .col-12
                      .p-4
                        p.text-center #[b Manejo sanitario]
                        p.text-center.mb-0 Vacunación, desparasitación, control de vectores, manejo de emergencias y atención de animales enfermos.
                .tarjeta.tarjeta--slyder
                  .row.justify-content-center.mb-3
                    .col-12
                      img(src="@/assets/curso/tema1/img44.png", alt="alt").img100.m-auto
                  .row
                    .col-12
                      .p-4
                        p.text-center #[b Instalaciones]
                        p.text-center.mb-0 Limpieza diaria, desinfección programada, manejo de estiércol y efluentes.
                .tarjeta.tarjeta--slyder
                  .row.justify-content-center.mb-3
                    .col-12
                      img(src="@/assets/curso/tema1/img45.png", alt="alt").img100.m-auto
                  .row
                    .col-12
                      .p-4
                        p.text-center #[b Alimentación y nutrición]
                        p.text-center.mb-0 Horarios, raciones estandarizadas, revisión de calidad del alimento.
                .tarjeta.tarjeta--slyder
                  .row.justify-content-center.mb-3
                    .col-12
                      img(src="@/assets/curso/tema1/img46.png", alt="alt").img100.m-auto
                  .row
                    .col-12
                      .p-4
                        p.text-center #[b Bienestar animal]
                        p.text-center.mb-0 Entrenamiento amable, métodos de contención seguros, respeto por el comportamiento natural.
                .tarjeta.tarjeta--slyder
                  .row.justify-content-center.mb-3
                    .col-12
                      img(src="@/assets/curso/tema1/img47.png", alt="alt").img100.m-auto
                  .row
                    .col-12
                      .p-4
                        p.text-center #[b Bioseguridad]
                        p.text-center.mb-0 Cuarentena, control de visitantes y vehículos, uso de puntos de desinfección.
                .tarjeta.tarjeta--slyder
                  .row.justify-content-center.mb-3
                    .col-12
                      img(src="@/assets/curso/tema1/img48.png", alt="alt").img100.m-auto
                  .row
                    .col-12
                      .p-4
                        p.text-center #[b Uso de medicamentos]
                        p.text-center.mb-0 Almacenamiento seguro, administración responsable, registros obligatorios.
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Sistema de registro y seguimiento
          p La implementación de las BPG debe ser sustentada con documentación verificable, que permita hacer trazabilidad, evaluar el cumplimiento y facilitar auditorías. Los registros deben mantenerse actualizados, organizados y disponibles para revisión.
    .row.bg-c9.mb-4
      .col-12
        .px-5
          .row.justify-content-center
            .col-md-6.col-lg-3.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img24.svg', style="width: 150px").m-auto
                p.text-center #[b Sanitarios]
                p.text-center.mb-0 Vacunación, desparasitación, tratamientos, cojeras, cólicos, emergencias.
            .col-md-6.col-lg-3.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img25.svg', style="width: 150px").m-auto
                p.text-center #[b De manejo]
                p.text-center.mb-0 Alimentación, limpieza de pesebreras, entrenamiento, jornadas de trabajo, compras y almacenamiento.
            .col-md-6.col-lg-3.mb-4.mb-md-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img26.svg', style="width: 150px").m-auto
                p.text-center #[b De bienestar animal]
                p.text-center.mb-0 Condición corporal, revisión de cascos, evaluación de comportamiento, indicadores de bienestar (según metodología ICA).
            .col-md-6.col-lg-3
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img21.svg', style="width: 150px").m-auto
                p.text-center #[b Administrativos]
                p.text-center.mb-0 Ingreso y salida de animales, inventarios de insumos, formación y capacitación del personal.
          Separador
          #t_1_7
          .titulo-segundo.color-acento-contenido
            h2 1.7 Metodologías de desarrollo y programación
          .row.align-items-center.mb-5
            .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
              p La implementación de las Buenas Prácticas Ganaderas (BPG) en la producción primaria de équidos es un proceso progresivo y estructurado que tiene como finalidad garantizar el bienestar animal, la bioseguridad, la inocuidad de los productos, la sostenibilidad ambiental y la seguridad del personal involucrado.
              .row.justify-content-center.mb-3
                .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img28.svg', style="width: 140px").m-auto
                .col-lg.bg-c2
                  .row.h-100.align-items-center
                    .col-12
                      .p-4
                        p.mb-0 Este proceso busca consolidar un sistema equino organizado, seguro y alineado con los estándares exigidos por el ICA, con miras a la certificación oficial en BPG, conforme a la Resolución 7953 de 2017. La metodología de desarrollo se fundamenta en siete etapas que permiten diagnosticar el estado actual del predio, identificar brechas, formular planes de mejora, ejecutar acciones correctivas y solicitar la auditoría oficial.
            .col-lg-4(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img49.png', style="width: 400px").m-auto
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Etapa 1: Diagnóstico inicial del predio
          p.mb-0 El diagnóstico inicial es el punto de partida para identificar necesidades, riesgos y oportunidades de mejora. Debe aplicarse mediante listas de chequeo basadas en la normativa ICA y en la metodología oficial de evaluación del bienestar equino.
    .row.bg-c8.mb-5
      .col-12
        .p-5
          .tarjeta.tarjeta--azul1.p-4.shadow
            SlyderA(tipo="b")
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img50.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b A. Manejo de los équidos]
                  p.mb-0 Estado sanitario, condición corporal, nutrición, cascos, dentadura, piel, locomoción, comportamiento, rutinas y condiciones de bienestar (Resolución 136/2020).
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img51.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b B. Personal]
                  p.mb-0 Nivel de capacitación, uso de EPP, riesgos laborales, necesidades formativas.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img52.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b C. Infraestructura]
                  p.mb-0 Estado de pesebreras, corrales, ventilación, drenaje, áreas de cuarentena, delimitación del predio, control de acceso, disponibilidad de agua y comederos.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img53.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b D. Medicamentos e insumos]
                  p.mb-0 Almacenamiento seguro, clasificación, eliminación de productos vencidos, control de inventarios.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img54.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b E. Alimentación]
                  p.mb-0 Calidad y almacenamiento del forraje, limpieza de comederos, acceso permanente a agua.
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img55.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b F. Bioseguridad]
                  p.mb-0 Registros de ingreso, medidas preventivas, cuarentena, dispositivos de desinfección.
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Etapa 2: revisión de normatividad vigente
    .row.bg-c9.mb-5
      .col-12
        .px-5
          .row.align-items-center.mb-5
            .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
              p Con el diagnóstico realizado, se debe hacer una revisión exhaustiva de la normativa aplicable, para establecer el marco técnico que rige la producción equina certificada.
              .row.justify-content-center.mb-3
                .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img29.svg', style="width: 130px").m-auto
                .col-lg.bg-c13
                  .row.h-100.align-items-center
                    .col-12
                      .p-4
                        p Normas clave a considerar:
                        ul.lista-ul.mb-0
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Resolución ICA 7953 de 2017: requisitos para certificación en BPG.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Resolución 136 de 2020: condiciones mínimas de bienestar animal por especie.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Manual ICA de Bienestar Animal en Transporte (2022).
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Ley 1774 de 2016: reconocimiento de los animales como seres sintientes.
                          li.d-flex.mb-0
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Guías técnicas ICA: bioseguridad, manejo, nutrición y sanidad en équidos.
            .col-lg-4(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img56.png', style="width: 400px").m-auto
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Etapa 3: aplicación de la lista de chequeo ICA
          p.mb-4 El productor debe aplicar la lista oficial del ICA, cuyo objetivo es identificar el nivel de cumplimiento y establecer brechas respecto a los estándares exigidos.
          .row.justify-content-center.mb-4
            .col-md-6.mb-4.mb-md-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img30.svg', style="width: 150px").m-auto
                p.text-center #[b Objetivos de esta etapa]
                ul.lista-ul.mb-0
                  li.d-flex
                    i.fa-solid.fa-circle-right
                    |
                    p.mb-0 Identificar no conformidades.
                  li.d-flex
                    i.fa-solid.fa-circle-right
                    |
                    p.mb-0 Priorizar acciones de mejora.
                  li.d-flex.mb-0
                    i.fa-solid.fa-circle-right
                    |
                    p.mb-0 Definir el nivel de avance frente a requisitos sanitarios, ambientales y de bienestar animal.
            .col-md-6
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img31.svg', style="width: 150px").m-auto
                p.text-center #[b Clasificación de ítems]
                .row.justify-content-center
                  .col-auto
                    ul.lista-ul.mb-0
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 Cumple.
                      li.d-flex
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 No cumple.
                      li.d-flex.mb-0
                        i.fa-solid.fa-circle-right
                        |
                        p.mb-0 No aplica.
          p.mb-5 Las no conformidades críticas deben ser corregidas de forma inmediata.
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Etapa 4: diseño del plan de mejora
          p.mb-4 Con base en el diagnóstico y la lista de chequeo, se diseña un plan de mejora ajustado a las condiciones del predio.
          TabsB.color-primario
            .p-4.p-md-5(titulo="Contenido del plan" :icono="require('@/assets/curso/tema1/img32.svg')")
              .row.justify-content-center
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img57.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Contenido del plan]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Acciones a corto, mediano y largo plazo.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Asignación de responsables.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Recursos requeridos (humanos, físicos y económicos).
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Indicadores de seguimiento.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Fechas de cumplimiento estimadas.
            .p-4.p-md-5(titulo="Ejemplos de acciones correctivas" :icono="require('@/assets/curso/tema1/img32.svg')")
              .row.justify-content-center
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img58.png', style="width: 400px").m-auto
                .col-lg-5
                  p #[b Ejemplos de acciones correctivas]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Adecuar pesebreras conforme a dimensiones reglamentarias.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Implementar área funcional de cuarentena.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Clasificar medicamentos y eliminar los vencidos.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Establecer programa de manejo de estiércol.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Capacitar al personal en primeros auxilios equinos.
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Etapa 5: ejecución del plan de implementación
    .row.align-items-center.mb-5
      .col-lg-9.mb-4.mb-lg-0(data-aos="fade-right")
        p En esta etapa se llevan a cabo las acciones previstas en el plan, garantizando su documentación y trazabilidad.
        AcordionA(tipo="a" clase-tarjeta="tarjeta tarjeta--azul")
          .row(titulo="Actividades principales")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Construcción y adecuación de instalaciones.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Implementación de protocolos escritos.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Establecimiento de rutinas operativas: alimentación, limpieza, bioseguridad.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Actualización de registros sanitarios y administrativos.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Ejecución del plan de capacitación.
          .row(titulo="Documentación")
            .col-12
              p Cada actividad debe quedar documentada con:
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registros firmados.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Evidencias fotográficas.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Listas de asistencia a capacitaciones.
      .col-lg-3.d-none.d-lg-block(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img59.png', style="width: 300px").m-auto
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Etapa 6: seguimiento y monitoreo
    .row.align-items-center.mb-5
      .col-lg-4(data-aos="fade-right")
        figure
          img(src='@/assets/curso/tema1/img60.png', style="width: 400px").m-auto
      .col-lg-8.mb-4.mb-lg-0(data-aos="fade-left")
        p Esta fase asegura que las mejoras se mantengan en el tiempo y que el predio avance hacia el cumplimiento total. Las acciones clave de monitoreo:
        .row.justify-content-center.mb-3
          .col-lg-auto.d-none.d-lg-block.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img33.svg', style="width: 150px").m-auto
          .col-lg.bg-c13
            .row.h-100.align-items-center
              .col-12
                .p-4
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Revisiones periódicas de los protocolos (semanales o mensuales).
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Aplicación de listas de chequeo internas.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Evaluación continua de indicadores de bienestar animal:
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Condición corporal.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Cojeras.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Hidratación.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Estado de cascos.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Lesiones.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Comportamiento.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Expresión de necesidades etológicas.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Auditorías internas.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Ajustes de actividades con base en los hallazgos.
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Etapa 7: solicitud de auditoría ICA
    .row.align-items-center
      .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
        p Cuando el predio haya alcanzado al menos un 80 % de cumplimiento, se puede solicitar formalmente la auditoría del ICA.
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img34.svg', style="width: 140px").m-auto
          .col-lg.bg-c2
            .row.h-100.align-items-center
              .col-12
                .p-4
                  p Condiciones y resultados esperados:
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 El ICA debe programar la visita en un plazo de 15 días.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 El resultado de la auditoría puede ser:
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Certificable: si el predio cumple con todos los requisitos.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Aplazado: si persisten no conformidades críticas.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 El concepto técnico será consignado en un informe oficial, el cual quedará archivado en el expediente del predio.
      .col-lg-4(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img61.png', style="width: 400px").m-auto
    Separador
    #t_1_8
    .titulo-segundo.color-acento-contenido
      h2 1.8 Cronograma para la implementación de BPG y Bienestar Animal en équidos
    .row.align-items-center.mb-5
      .col-lg-4(data-aos="fade-right")
        figure
          img(src='@/assets/curso/tema1/img62.png', style="width: 400px").m-auto
      .col-lg-8.mb-4.mb-lg-0(data-aos="fade-left")
        p El cronograma es una herramienta clave en el proceso de implementación de las Buenas Prácticas Ganaderas (BPG) en predios dedicados a la producción primaria de équidos. Su función es organizar y visualizar todas las actividades necesarias para cumplir con los requisitos de certificación establecidos por el Instituto Colombiano Agropecuario (ICA), garantizando además la sostenibilidad y el bienestar de los animales. Un cronograma eficiente permite:
        .row.justify-content-center.mb-3
          .col-lg-auto.d-none.d-lg-block.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img35.svg', style="width: 150px").m-auto
          .col-lg.bg-c13
            .row.h-100.align-items-center
              .col-12
                .p-4
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Identificar las actividades críticas.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Establecer plazos realistas para cada etapa.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Coordinar al personal responsable.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Optimizar el uso de los recursos disponibles.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Verificar el avance del proceso y detectar retrasos.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Asegurar el cumplimiento de estándares sanitarios, de bioseguridad y de bienestar animal.
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Estructura del cronograma
    p.mb-4 El cronograma debe estructurarse en función del estado inicial del predio, considerando tres fases principales:
    .titulo-sexto.color-acento-contenido
      p.mb-0 #[b Tabla 3.] Estructura del cronograma
    .tabla-a.color-acento-botones.mb-5
      table
        thead
          tr
            th(width="30%") Fase
            th(width="30%") Duración estimada
            th Objetivo
        tbody.bg-white
          tr
            td.txt-p1 Corto plazo
            td.txt-p1 0 a 3 meses
            td.txt-p1 Acciones urgentes y adecuaciones mínimas.
          tr
            td.txt-p1 Mediano plazo
            td.txt-p1 3 a 6 meses
            td.txt-p1 Implementación de protocolos, registros y rutinas.
          tr
            td.txt-p1 Largo plazo
            td.txt-p1 6 a 12 meses
            td.txt-p1 Consolidación del sistema y preparación para auditoría.
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Actividades clave para el cronograma
    p.mb-4 El plan debe incluir actividades esenciales que respondan directamente a los requerimientos normativos y técnicos del ICA:
    .row.bg-c8.mb-4
      .col-12
        .p-5
          .tarjeta.tarjeta--azul1.p-4.shadow
            SlyderA(tipo="b")
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  p #[b Recolección y organización documental]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Inventario de animales con identificación individual.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Registros administrativos existentes.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Fotografía del estado inicial del predio.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Plan sanitario firmado por médico veterinario.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Registros de tratamientos, vacunas y desparasitaciones.
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img63.png', style="width: 400px").m-auto
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  p #[b Inspección inicial del predio]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Revisión de instalaciones (pesebreras, corrales, cuarentena, enfermería).
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Acceso y calidad del agua.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Estado de comederos, bebederos y camas.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Evaluación del estado físico y sanitario de los équidos.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Nivel de capacitación del personal.
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img64.png', style="width: 400px").m-auto
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  p #[b Diagnóstico técnico integral]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Aplicación de la lista de chequeo según:
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Resolución ICA 7953 de 2017.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Resolución ICA 136 de 2020.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Manual ICA de Bienestar en el Transporte.
                    li.d-flex.mb-0.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Metodología ICA para bienestar en équidos.
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img65.png', style="width: 400px").m-auto
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  p #[b Diseño del plan de acción]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Elaboración de protocolos.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Asignación de responsables.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Ajustes de infraestructura y medidas de bioseguridad.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Implementación de programas sanitarios y de bienestar.
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img66.png', style="width: 400px").m-auto
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  p #[b Capacitación del personal]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 En bienestar animal, manejo amable, bioseguridad, sanidad y contención segura.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Reconocimiento del dolor y signos clínicos.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Uso de registros y procedimientos conforme a BPG.
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img67.png', style="width: 400px").m-auto
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  p #[b Divulgación de lineamientos]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Reuniones formativas con operarios, propietarios, administradores y veterinario responsable.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Evidencias de comprensión y asistencia documentada.
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img68.png', style="width: 400px").m-auto
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  p #[b Implementación progresiva de actividades]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Limpieza diaria de pesebreras y bebederos.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Rutinas de alimentación.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Revisión periódica de cascos.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Manejo de estiércol y residuos.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Aplicación de protocolos de bioseguridad.
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img69.png', style="width: 400px").m-auto
              .row.justify-content-center.align-items-center.my-lg-4
                .col-lg-5.mb-4.mb-lg-0
                  p #[b Seguimiento y verificación]
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Auditorías internas.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Uso de listas de chequeo.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Evaluación periódica de indicadores clave:
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Condición corporal.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Cojeras.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Hidratación.
                    li.d-flex.ms-4
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Lesiones.
                    li.d-flex.ms-4.mb-0
                      i.fa-solid.fa-circle
                      |
                      p.mb-0 Comportamiento.
                .col-lg-5
                  figure
                    img(src='@/assets/curso/tema1/img70.png', style="width: 400px").m-auto
    p.mb-4 El siguiente cronograma organiza las acciones clave del plan de implementación y distribuye las responsabilidades para avanzar de forma ordenada desde el diagnóstico hasta la auditoría del ICA. Permite identificar tiempos, responsables y tareas esenciales para asegurar el cumplimiento progresivo de los requisitos establecidos.
    .titulo-sexto.color-acento-contenido
      p.mb-0 #[b Tabla 4.] Ejemplo de cronograma
    .tabla-a.color-acento-botones
      table
        thead
          tr
            th Actividad
            th Responsable
            th Plazo estimado
        tbody.bg-white
          tr
            td.txt-p1 Diagnóstico inicial del predio
            td.txt-p1 Veterinario / Administrador
            td.txt-p1 Semana 1
          tr
            td.txt-p1 Ajustes de infraestructura mínima
            td.txt-p1 Administrador
            td.txt-p1 Mes 1
          tr
            td.txt-p1 Capacitación del personal
            td.txt-p1 Veterinario
            td.txt-p1 Mes 1-2
          tr
            td.txt-p1 Implementación de protocolos escritos
            td.txt-p1 Todo el equipo
            td.txt-p1 Mes 2-4
          tr
            td.txt-p1 Aplicación plena de registros
            td.txt-p1 Administrador
            td.txt-p1 M3w 3-6
          tr
            td.txt-p1 Auditoría interna
            td.txt-p1 Veterinario
            td.txt-p1 Mes 6-7
          tr
            td.txt-p1 Solicitud de auditoría ICA
            td.txt-p1 Propietario
            td.txt-p1 Mes 9-12
    Separador
    #t_1_9
    .titulo-segundo.color-acento-contenido
      h2 1.9 Lista de chequeo
    .row.align-items-center.mb-5
      .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img36.svg', style="width: 140px").m-auto
          .col-lg.bg-c2
            .row.h-100.align-items-center
              .col-12
                .p-4
                  p.mb-0 Las listas de chequeo constituyen una herramienta técnica esencial para la verificación organizada, objetiva y sistemática del cumplimiento de los requisitos establecidos para la implementación de las Buenas Prácticas Ganaderas (BPG) en équidos. Su principal finalidad es comprobar si las actividades programadas se ejecutan correctamente, facilitando así el control, seguimiento y mejora continua de los procesos relacionados con la sanidad animal, la bioseguridad, el bienestar, la infraestructura, el manejo, la alimentación y la documentación técnica del predio.
        p.mb-0 Estas listas deben estar alineadas con los estándares oficiales definidos por el ICA, particularmente los contenidos en la Resolución 7953 de 2017, la Resolución 136 de 2020 y las guías técnicas de bienestar animal en équidos. Para su aplicación, es fundamental contar con el criterio técnico del profesional responsable, quien debe evaluar con objetividad si cada ítem cumple con lo estipulado.
      .col-lg-4(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img71.png', style="width: 400px").m-auto
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Funciones de la lista de chequeo
    p.mb-4 A continuación, se presentan los usos centrales de la lista de chequeo como herramienta técnica para revisar el cumplimiento, orientar decisiones y apoyar el avance del plan de implementación en el predio equino.
    .row.bg-c14.mb-5
      .col-12
        .px-5
          .row.align-items-center.mb-5
            .col-lg-3.mb-4.mb-lg-0(data-aos="fade-right")
              figure
                img(src='@/assets/curso/tema1/img72.png', style="width: 300px").m-auto
            .col-lg-9(data-aos="fade-left")
              SlyderF(columnas="col-md-6 col-xl-4")
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img37.svg", alt="alt").img100.m-auto
                  p.text-center #[b Verificar cumplimiento normativo]
                  p.text-center.mb-0 Confirma si se aplican correctamente las disposiciones del ICA.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img38.svg", alt="alt").img100.m-auto
                  p.text-center #[b Supervisar el plan de implementación]
                  p.text-center.mb-0 Monitorea el avance real de las acciones planificadas.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img39.svg", alt="alt").img100.m-auto
                  p.text-center #[b Identificar fallas o desviaciones]
                  p.text-center.mb-0 Detecta brechas entre el estado actual y los requisitos técnicos.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img40.svg", alt="alt").img100.m-auto
                  p.text-center #[b Facilitar auditorías]
                  p.text-center.mb-0 Sirve como soporte en auditorías internas y externas.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img41.svg", alt="alt").img100.m-auto
                  p.text-center #[b Establecer planes de mejora continua]
                  p.text-center.mb-0 Permite planificar acciones correctivas.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img42.svg", alt="alt").img100.m-auto
                  p.text-center #[b Preparar la certificación ICA]
                  p.text-center.mb-0 Evalúa si el predio cumple con el mínimo requerido para ser certificado (>80 %).
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Características técnicas
          .row.align-items-center
            .col-lg-8.mb-4.mb-lg-0(data-aos="fade-right")
              p Una lista de chequeo completa y funcional debe incluir:
              .row.justify-content-center.mb-3
                .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img43.svg', style="width: 150px").m-auto
                .col-lg.bg-c13
                  .row.h-100.align-items-center
                    .col-12
                      .p-4
                        p Normas clave a considerar:
                        ul.lista-ul.mb-0
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Procedimientos y subprocesos de sanidad animal.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Condiciones de instalaciones, higiene y bioseguridad.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Manejo de alimentación (frecuencia, calidad, almacenamiento).
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Indicadores concretos de bienestar animal (cinco libertades).
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Uso y manejo de medicamentos (registro ICA, dosis, retiro).
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Almacenamiento adecuado de insumos veterinarios y agrícolas.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Existencia y actualización de registros obligatorios.
                          li.d-flex.mb-0
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Condiciones y procedimientos para el transporte o movilización de équidos.
            .col-lg-4(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img73.png', style="width: 400px").m-auto
    .row.bg-c9.mb-4
      .col-12
        .px-5
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Tipo de evaluación
          p.mb-4 Cada ítem debe calificarse según el nivel de cumplimiento observado:
          .row.justify-content-center.mb-4
            .col-md-6.col-lg-4.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img44.svg', style="width: 100px").m-auto
                p.text-center #[b Cumple]
                p.text-center.mb-0 El requisito se cumple completamente conforme a la normativa.
            .col-md-6.col-lg-4.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img45.svg', style="width: 100px").m-auto
                p.text-center #[b No cumple]
                p.text-center.mb-0 El requisito no se implementa o se realiza de forma incorrecta.
            .col-md-6.col-lg-4
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img46.svg', style="width: 100px").m-auto
                p.text-center #[b No aplica]
                p.text-center.mb-0 El ítem no corresponde al tipo de producción evaluada.
          .row.align-items-center.mb-5
            .col-lg-4.mb-4.mb-lg-0(data-aos="fade-right")
              figure
                img(src='@/assets/curso/tema1/img74.png', style="width: 400px").m-auto
            .col-lg-8(data-aos="fade-left")
              p Cada evaluación debe ir acompañada de:
              .row.justify-content-center
                .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img47.svg', style="width: 130px").m-auto
                .col-lg.bg-c2
                  .row.h-100.align-items-center
                    .col-12
                      .p-4
                        ul.lista-ul.mb-0
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Evidencia fotográfica.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Registros físicos o digitales (tratamientos, alimentación, limpieza, capacitaciones, etc.).
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Observaciones del auditor o técnico.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Recomendaciones de mejora.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Fecha de revisión.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 Nombre y firma del responsable.
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Ejemplos de preguntas de lista de chequeo
          .row.align-items-center
            .col-lg-8.order-2.order-lg-1(data-aos="fade-right")
              p Estas preguntas apoyan la revisión diaria y periódica del predio, permitiendo confirmar condiciones básicas de bienestar, sanidad, manejo y cumplimiento de los protocolos establecidos.
              .row.justify-content-center.mb-3
                .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img48.svg', style="width: 130px").m-auto
                .col-lg.bg-c13
                  .row.h-100.align-items-center
                    .col-12
                      .p-4
                        ul.lista-ul.mb-0
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 ¿El animal tiene agua limpia y disponible en todo momento?
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 ¿Las pesebreras están limpias, secas, ventiladas y seguras?
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 ¿Los cascos están en buen estado o requieren recorte/herraje?
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 ¿Se aplicaron las vacunas y desparasitaciones dentro de los plazos establecidos?
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 ¿Se respetan los principios de bienestar animal y las cinco libertades?
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 ¿Las instalaciones están libres de objetos peligrosos?
                          li.d-flex.mb-0
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 ¿El personal aplica los protocolos actualizados correctamente?
            .col-lg-4.order-1.order-lg-2.mb-4.mb-lg-0(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img75.png', style="width: 400px").m-auto
    .row.bg-c14.mb-4
      .col-12
        .px-5
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Organización por áreas
          p.mb-4(data-aos="fade-right") Para facilitar su aplicación, las listas de chequeo deben organizarse por componentes temáticos:
          .row.align-items-center.mb-5
            .col-lg-3.mb-4.mb-lg-0(data-aos="fade-right")
              figure
                img(src='@/assets/curso/tema1/img76.png', style="width: 300px").m-auto
            .col-lg-9(data-aos="fade-left")
              SlyderF(columnas="col-md-6 col-xl-4")
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img49.svg", alt="alt").img100.m-auto
                  p.text-center #[b Sanidad animal]
                  p.text-center.mb-0 Vacunación, desparasitación, atención de cólicos, protocolos sanitarios.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img50.svg", alt="alt").img100.m-auto
                  p.text-center #[b Alimentación]
                  p.text-center.mb-0 Calidad del forraje, acceso al agua, suplementación, frecuencia de suministro.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img51.svg", alt="alt").img100.m-auto
                  p.text-center #[b Infraestructura]
                  p.text-center.mb-0 Condiciones de pesebreras, corrales, drenaje, ventilación, cuarentena.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img52.svg", alt="alt").img100.m-auto
                  p.text-center #[b Bienestar animal]
                  p.text-center.mb-0 Condición corporal, lesiones, comportamiento, libertad de movimiento.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img53.svg", alt="alt").img100.m-auto
                  p.text-center #[b Bioseguridad]
                  p.text-center.mb-0 Control de ingreso, pediluvios, cuarentena, desinfección.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img54.svg", alt="alt").img100.m-auto
                  p.text-center #[b Medicamentos]
                  p.text-center.mb-0 Uso racional, almacenamiento seguro, prescripciones, fechas de vencimiento.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img55.svg", alt="alt").img100.m-auto
                  p.text-center #[b Documentación]
                  p.text-center.mb-0 Registros sanitarios, administrativos, capacitaciones, inventarios.
                .tarjeta.tarjeta--slyder.p-4
                  .row.justify-content-center.mb-3
                    .col-8
                      img(src="@/assets/curso/tema1/img56.svg", alt="alt").img100.m-auto
                  p.text-center #[b Transporte]
                  p.text-center.mb-0 Condiciones de viaje, densidad, hidratación, tiempo de ayuno y descanso.
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Uso de datos para decisiones
          .row.align-items-center
            .col-lg.mb-4.mb-lg-0(data-aos="fade-right")
              p.mb-0 Los resultados obtenidos pueden organizarse y visualizarse a través de herramientas gráficas para facilitar la interpretación y la toma de decisiones:
            .col-lg-auto.d-none.d-lg-block(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img4.svg', style="width: 150px").m-auto
          .row.justify-content-center
            .col-md-6.col-lg-4.p-4
              .row
                .col-12.px-0
                  figure
                    img(src='@/assets/curso/tema1/img77.png', style="width: 440px").m-auto
              .row.bg-c15
                .col-12
                  .p-4
                    p.text-center #[b Diagramas circulares]
                    p.mb-0 Distribución de cumplimiento por área.
            .col-md-6.col-lg-4.p-4
              .row
                .col-12.px-0
                  figure
                    img(src='@/assets/curso/tema1/img78.png', style="width: 440px").m-auto
              .row.bg-c15
                .col-12
                  .p-4
                    p.text-center #[b Gráficas de barras]
                    p.mb-0 Comparación entre periodos o componentes.
            .col-md-6.col-lg-4.p-4
              .row
                .col-12.px-0
                  figure
                    img(src='@/assets/curso/tema1/img79.png', style="width: 440px").m-auto
              .row.bg-c15
                .col-12
                  .p-4
                    p.text-center #[b Series temporales]
                    p.mb-0 Seguimiento de avances mensuales o bimestrales.
    .row.align-items-center
      .col-lg-8.order-2.order-lg-1(data-aos="fade-right")
        p Estas representaciones permiten:
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img57.svg', style="width: 130px").m-auto
          .col-lg.bg-c13
            .row.h-100.align-items-center
              .col-12
                .p-4
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Evaluar el nivel de avance del plan de implementación.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Medir la efectividad de los protocolos aplicados.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Detectar riesgos sanitarios o críticos persistentes.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Confirmar si se alcanza el umbral mínimo de cumplimiento para certificación (80 % o más).
      .col-lg-4.order-1.order-lg-2.mb-4.mb-lg-0(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img80.png', style="width: 400px").m-auto
    Separador
    #t_1_10
    .titulo-segundo.color-acento-contenido
      h2 1.10 Criterios de cumplimiento
    .row.align-items-center.mb-4
      .col-lg-4.mb-4.mb-lg-0(data-aos="fade-right")
        figure
          img(src='@/assets/curso/tema1/img81.png', style="width: 400px").m-auto
      .col-lg-8(data-aos="fade-left")
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img58.svg', style="width: 120px").m-auto
          .col-lg.bg-c13
            .row.h-100.align-items-center
              .col-12
                .p-4
                  p.mb-0 Los criterios de cumplimiento en Buenas Prácticas Ganaderas (BPG) para équidos se establecen a partir de los componentes técnicos, sanitarios, de bienestar animal, bioseguridad y manejo que conforman el sistema de producción equina. Estos criterios permiten evaluar de forma objetiva y estandarizada el nivel de adopción de las BPG en fincas, pesebreras, criaderos, centros ecuestres o cualquier unidad dedicada al manejo de équidos.
        p Su aplicación se realiza mediante listas de chequeo oficiales, diseñadas específicamente para esta especie, que funcionan como herramientas de diagnóstico, evaluación, seguimiento y mejora continua. Estas listas discriminan aspectos clave como:
        .row
          .col-12
            .p-4.bg-c13.rounded
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Alimentación.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Sanidad.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Instalaciones.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Manejo y trabajo.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Transporte.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Bienestar animal.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Documentación obligatoria.
    .row.bg-c9.mb-4
      .col-12
        .px-5
          p.mb-5 Los criterios han sido definidos por el ente regulador oficial responsable de verificar y certificar el cumplimiento de las BPG en Colombia: el Instituto Colombiano Agropecuario (ICA). Este organismo establece los parámetros mínimos aceptables, puntos críticos y requisitos obligatorios que deben cumplir las unidades productivas para ser aprobadas o certificadas.
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Clasificación de los puntos de control
          .row.align-items-center.mb-4
            .col-lg.mb-4.mb-lg-0(data-aos="fade-right")
              p.mb-0 Los criterios de cumplimiento se basan en los puntos de control definidos en la Resolución ICA 7953 de 2017. Cada punto se clasifica según su nivel de importancia:
            .col-lg-auto.d-none.d-lg-block(data-aos="fade-left")
              figure
                img(src='@/assets/curso/tema1/img4.svg', style="width: 150px").m-auto
          .row.justify-content-center.mb-5
            .col-md-6.col-lg-4.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img59.svg', style="width: 150px").m-auto
                p.text-center #[b Criterio Fundamental (F)]
                p Requisito obligatorio. Su incumplimiento implica un riesgo grave para la sanidad, la inocuidad o el bienestar animal.
                p.mb-0 Un predio con no conformidades fundamentales no puede ser certificado hasta que sean corregidas.
            .col-md-6.col-lg-4.mb-4.mb-lg-0
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img59.svg', style="width: 150px").m-auto
                p.text-center #[b Criterio Mayor (My)]
                p Requisito esencial. Varias no conformidades mayores afectan directamente la calificación del predio.
                p.mb-0 La certificación puede ser rechazada si se supera el porcentaje permitido de incumplimiento.
            .col-md-6.col-lg-4
              .p-4.bg-c10
                figure.mb-3
                  img(src='@/assets/curso/tema1/img59.svg', style="width: 150px").m-auto
                p #[b Criterio Menor (Mn)]
                p Requisito complementario. Su incumplimiento no impide la certificación por sí solo.
                p.mb-0 Su acumulación evidencia debilidades en la implementación del sistema y genera planes de mejora obligatorios.
          .row.mb-4.ms-0
            .col-auto.bg-c7(data-aos="fade-left")
              .row.align-items-end
                .col-auto.px-0
                  figure
                    img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
                .col.ps-0
                  h3.mb-2.ms-3 Sistema de evaluación por ítem
          .row.align-items-center
            .col-lg-4.mb-4.mb-lg-0(data-aos="fade-right")
              figure
                img(src='@/assets/curso/tema1/img82.png', style="width: 400px").m-auto
            .col-lg-8(data-aos="fade-left")
              p Cada ítem de la lista de chequeo se evalúa bajo tres opciones estandarizadas:
              .row.justify-content-center
                .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
                  figure
                    img(src='@/assets/curso/tema1/img60.svg', style="width: 120px").m-auto
                .col-lg.bg-c2
                  .row.h-100.align-items-center
                    .col-12
                      .p-4
                        ul.lista-ul.mb-0
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 C (Cumple): el requisito se cumple completamente.
                          li.d-flex
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 NC (No cumple): el requisito no se cumple o lo hace de forma parcial.
                          li.d-flex.mb-0
                            i.fa-solid.fa-circle-right
                            |
                            p.mb-0 NA (No aplica): el requisito no corresponde al tipo de predio o actividad evaluada.
    .row
      .col-12
        .p-4.bg-c16
          .row.align-items-center.justify-content-center
            .col-lg.mb-4.mb-lg-0
              .row.align-items-center
                .col-md-auto.mb-4.mb-md-0
                  figure
                    img(src='@/assets/curso/tema1/img61.svg', alt='', style='width: 80px').m-auto
                .col-md
                  p.text-white.mb-0 #[b Anexo: CUADRO — REQUISITOS BPG EN ÉQUIDOS]
                  p.text-white.mb-0 A continuación se presenta el formato con ajustes de escritura, precisión técnica y claridad, sin alterar la estructura original.
            .col-auto
              a.boton.boton--b(:href="obtenerLink('/downloads/Anexo_cuadro_requisitos_BPG_en_equipos.pdf')" target="_blank")
                span Realizar
                i.fas.fa-download
    Separador
    #t_1_11
    .titulo-segundo.color-acento-contenido
      h2 1.11 Documentación
    .row.align-items-center.mb-5
      .col-lg-8.order-2.order-lg-1(data-aos="fade-right")
        p La documentación es un componente obligatorio en la implementación de las Buenas Prácticas Ganaderas (BPG) en la producción primaria de équidos. Su adecuada gestión permite:
        .row.justify-content-center.mb-3
          .col-lg-auto.mb-4.mb-lg-0.pe-lg-0
            figure
              img(src='@/assets/curso/tema1/img62.svg', style="width: 130px").m-auto
          .col-lg.bg-c13
            .row.h-100.align-items-center
              .col-12
                .p-4
                  ul.lista-ul.mb-0
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Mantener un historial técnico confiable y actualizado.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Facilitar la toma de decisiones con base en evidencia.
                    li.d-flex
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Garantizar la trazabilidad sanitaria y productiva.
                    li.d-flex.mb-0
                      i.fa-solid.fa-circle-right
                      |
                      p.mb-0 Servir como soporte durante auditorías del ICA para la certificación o renovación.
        p.mb-0 La Resolución ICA 00007953 de 2017 establece que todo productor debe contar con registros específicos sobre sanidad, bioseguridad, alimentación, manejo, bienestar animal e identificación individual de cada equino.
      .col-lg-4.order-1.order-lg-2.mb-4.mb-lg-0(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img83.png', style="width: 400px").m-auto
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Registros obligatorios en la unidad productiva de équidos
    p.mb-4 Los registros deben mantenerse organizados, actualizados y disponibles, en formato físico o digital, con respaldo en papel para su presentación durante la auditoría oficial.
    .row.align-items-center
      .col-lg-9.mb-4.mb-lg-0(data-aos="fade-right")
        AcordionA(tipo="a" clase-tarjeta="tarjeta tarjeta--azul")
          .row(titulo="A. Eventos sanitarios")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Vacunaciones aplicadas (según normativa vigente).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Desparasitación interna y externa.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Tratamientos veterinarios (art. 9 - Resolución 7953).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Casos clínicos: cólico, fiebre, heridas, cojeras, entre otros.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Mortalidades, causa probable o confirmada (art. 7.4)Reportes de laboratorio o necropsias cuando corresponda.
          .row(titulo="B. Bioseguridad")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Ingreso y salida de personas, vehículos y animales (art. 7.2).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Limpieza y desinfección de instalaciones.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Desinfección de equipos, monturas y herramientas.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Control de plagas y roedores (art. 6.5).
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Temperatura diaria de almacenamiento de biológicos (art. 9.9).
          .row(titulo="C. Manejo y bienestar animal")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Evaluación periódica de condición corporal.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Revisión y recorte de cascos (herrado).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registros de entrenamiento, ejercicio y jornadas de trabajo (máx. 8 h o 60 km - art. 12.6).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Evaluación de comportamiento y signos de estrés.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registro de suministro de agua y alimento.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Aplicación del checklist de las Cinco Libertades.
      .col-lg-3.d-none.d-lg-block(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema1/img84.png', style="width: 300px").m-auto
    .row.align-items-center.mb-5
      .col-lg-3.d-none.d-lg-block(data-aos="fade-right")
        figure
          img(src='@/assets/curso/tema1/img85.png', style="width: 300px").m-auto
      .col-lg-9.mb-4.mb-lg-0(data-aos="fade-left")
        AcordionA(tipo="a" clase-tarjeta="tarjeta tarjeta--azul")
          .row(titulo="D. Manejo de instalaciones")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Limpieza de pesebreras, corrales y áreas de confinamiento.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Reposición de cama y revisión de ventilación.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Inspección de cercas, broches, estructuras y zonas de riesgo.
          .row(titulo="E. Registros administrativos")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Ingreso y salida de animales (movilización interna).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Inventario de medicamentos e insumos agropecuarios.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Inventario de alimentos y su almacenamiento.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Control de existencias y fechas de vencimiento.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registro de capacitaciones del personal.
          .row(titulo="F. Registro individual por animal (art. 11 - Identificación y trazabilidad)")
            .col-12
              p Cada equino debe contar con una ficha individual que contenga:
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Identificación única (microchip, hierro, tatuaje u otro autorizado).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Fecha de nacimiento (o estimada), sexo, raza y señas particulares.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Historial sanitario.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Historial de tratamientos veterinarios.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Historial de herrado y recorte de cascos.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Actividad productiva o tipo de uso.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registros de movilización dentro o fuera del predio.
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Documentación exigida para la certificación BPG en équidos (ICA)
    p.mb-4 Al solicitar la auditoría para certificación, el productor debe tener disponible la siguiente documentación técnica:
    .row.align-items-center
      .col-lg-9.mb-4.mb-lg-0(data-aos="fade-right")
        AcordionA(tipo="a" clase-tarjeta="tarjeta tarjeta--azul")
          .row(titulo="A. Documentación predial")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Certificado de uso del suelo para actividad pecuaria con équidos.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registro del predio como predio pecuario ante el ICA.
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registro sanitario del predio (si aplica según actividad).
          .row(titulo="B. Documentos de trazabilidad y movilización")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Identificación individual de todos los équidos (art. 11).
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Guías sanitarias de movilización expedidas por el ICA (originales y copias).
          .row(titulo="C. Documentación sanitaria")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Plan sanitario firmado por Médico Veterinario o MVZ (art. 7.1).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registro completo de tratamientos y medicamentos aplicados (art. 9.5).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Inventario actualizado de medicamentos veterinarios (art. 8.4 y art. 9).
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registros de desparasitación y control de plagas.
      .col-lg-3.d-none.d-lg-block(data-aos="fade-left")
        figure
          img(src='@/assets/curso/tema2/img4.png', style="width: 300px").m-auto
    .row.align-items-center.mb-5
      .col-lg-3.d-none.d-lg-block(data-aos="fade-right")
        figure
          img(src='@/assets/curso/tema2/img9.png', style="width: 300px").m-auto
      .col-lg-9.mb-4.mb-lg-0(data-aos="fade-left")
        AcordionA(tipo="a" clase-tarjeta="tarjeta tarjeta--azul")
          .row(titulo="D. Documentación de bioseguridad")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Procedimiento de ingreso de animales y visitantes.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Protocolo de cuarentena (art. 5.3).
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registro de desinfección de vehículos (cuando aplique - art. 7.3).
          .row(titulo="E. Documentación de bienestar animal")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Evidencia del cumplimiento de espacios mínimos por peso (art. 5.7).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registros de entrenamiento, carga de trabajo y tiempos de descanso (art. 12.6).
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Evaluaciones de comportamiento y condiciones ambientales.
          .row(titulo="F. Documentación del personal")
            .col-12
              ul.lista-ul.mb-0
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Registro de capacitación anual (art. 13.6).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Certificado médico anual del personal (art. 13.1).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Evidencia de uso de EPP (equipo de protección personal).
                li.d-flex.mb-0
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Afiliación del personal a seguridad social (art. 13.5).
    .row.mb-4.ms-0
      .col-auto.bg-c7(data-aos="fade-left")
        .row.align-items-end
          .col-auto.px-0
            figure
              img(src='@/assets/curso/tema1/img3.png', alt='', style='width: 40px; position: relative; left: 5px').m-auto
          .col.ps-0
            h3.mb-2.ms-3 Aplicación de normatividad y uso de la lista de chequeo ICA
    .row.align-items-center
      .col-lg-4.mb-4.mb-lg-0(data-aos="fade-right")
        figure
          img(src='@/assets/curso/tema1/img86.png', style="width: 400px").m-auto
      .col-lg-8(data-aos="fade-left")
        p Para cumplir con las exigencias del ICA y lograr la certificación BPG en équidos, el productor debe:
        .row
          .col-12
            .p-4.bg-c2.rounded
              p #[b a)] Consultar y aplicar la normativa vigente, especialmente:
              ul.lista-ul
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Resolución ICA 00007953 de 2017 (BPG en équidos).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Resolución 136 de 2020 (Manual Nacional de Bienestar Animal).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Resolución 20223040006915 de 2022 (Bienestar en el transporte animal).
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Metodología ICA de Evaluación de Bienestar Animal para équidos.
              p #[b b)] Aplicar la lista de chequeo oficial del ICA, con el fin de:
              ul.lista-ul
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Identificar no conformidades.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Detectar hallazgos críticos.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Reconocer evidencias faltantes.
                li.d-flex
                  i.fa-solid.fa-circle-right
                  |
                  p.mb-0 Establecer acciones de mejora obligatoria.
              p #[b c)] Construir un plan de mejora con base en los hallazgos de la evaluación técnica.
              p.mb-0 #[b d)] Solicitar la auditoría oficial, presentando toda la documentación exigida en formato verificable, organizada y actualizada.

</template>

<script>
export default {
  name: 'Tema1',
  data: () => ({
    mostrarIndicadorTarjetaAudio: true,
  }),
  mounted() {
    this.$nextTick(() => {
      this.$aosRefresh()
    })
  },
  updated() {
    this.$aosRefresh()
  },
}
</script>

<style lang="sass"></style>
